<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useScoring } from './composables/useScoring'
import HeroScoreRing from './components/HeroScoreRing.vue'
import RecordTestSheet from './components/RecordTestSheet.vue'
import { ensureLastScoreHydrated } from '@/shared/utils/lastScore'

interface HistoryEntry {
  id?: number
  composite?: number | string
  recordedDate?: string
  officialTestDate?: string
  testDate?: string
}

interface PlanDaySummary {
  day_of_week: number
  is_rest?: boolean
}

interface PlanSummary {
  plan_name?: string
  total_weeks?: number
  official_test_date?: string
  week_template?: PlanDaySummary[]
  week_template_phase2?: PlanDaySummary[]
}

interface QuickStat {
  label: string
  value: string
  detail: string
  tone?: 'good' | 'warn' | 'muted'
}

const { profile, pushUps, core, cardio, whtr, result, componentScores, calculate, clear } = useScoring()

const isSheetOpen = ref(false)
const calculationPulse = ref(0)
const quickStats = ref<QuickStat[]>([])
const trainingStatus = ref({
  detail: 'Generate a plan from your latest score.',
  progressText: '0%',
  progressPct: 0,
  hasPlan: false,
})

const anyExempt = computed(() => pushUps.exempt || core.exempt || cardio.exempt || whtr.exempt)

// Live preview ring. Empty until the user enters a value.
const hasInput = computed(() =>
  !!(pushUps.reps || core.reps || core.plankMin || core.plankSec ||
     cardio.runMin || cardio.runSec || cardio.shuttles || whtr.waist || whtr.height),
)

function parseJson<T>(key: string, fallback: T): T {
  try {
    return JSON.parse(localStorage.getItem(key) || '') as T
  } catch {
    return fallback
  }
}

function scoreNumber(entry: HistoryEntry): number | null {
  const score = Number(entry.composite)
  return Number.isFinite(score) ? score : null
}

function formatDate(value?: string): string | null {
  if (!value) return null
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function daysUntil(value?: string): number | null {
  if (!value) return null
  const due = new Date(`${value}T00:00:00`).getTime()
  if (Number.isNaN(due)) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.ceil((due - today.getTime()) / 86400000)
}

function loadQuickStats(): QuickStat[] {
  const history = parseJson<HistoryEntry[]>('af_pt_history', [])
  const scores = history.map(scoreNumber).filter((score): score is number => score !== null)
  const activePlan = parseJson<PlanSummary | null>('af_pt_active_plan', null)
  const planDue = activePlan?.official_test_date
  const historyDue = history
    .map(entry => entry.officialTestDate || entry.testDate)
    .find(Boolean)
  const nextDue = planDue || historyDue
  const dueIn = daysUntil(nextDue)

  const best = scores.length ? Math.max(...scores) : null
  const latest = scores[0] ?? null
  const previous = scores[1] ?? null
  const trend = latest !== null && previous !== null ? latest - previous : null

  return [
    {
      label: 'Next Due',
      value: formatDate(nextDue) || 'Not set',
      detail: dueIn === null ? 'Add an official test date' : dueIn >= 0 ? `${dueIn} days out` : `${Math.abs(dueIn)} days past`,
      tone: dueIn !== null && dueIn <= 30 ? 'warn' : 'muted',
    },
    {
      label: 'Best',
      value: best === null ? '--' : best.toFixed(1),
      detail: scores.length ? `${scores.length} saved scores` : 'No saved scores yet',
      tone: best !== null && best >= 90 ? 'good' : 'muted',
    },
    {
      label: 'Trend',
      value: trend === null ? '--' : `${trend >= 0 ? '+' : ''}${trend.toFixed(1)}`,
      detail: trend === null ? 'Needs two saved scores' : 'Latest vs previous',
      tone: trend !== null && trend >= 0 ? 'good' : trend !== null ? 'warn' : 'muted',
    },
  ]
}

function countTrainingProgress(plan: PlanSummary | null) {
  if (!plan?.week_template?.length) return { done: 0, total: 0 }

  const checked = parseJson<Record<string, boolean>>('af_pt_plan_checked', {})
  const totalWeeks = Math.max(1, Number(plan.total_weeks) || 1)
  let done = 0
  let total = 0

  for (let week = 1; week <= totalWeeks; week++) {
    const usePhase2 = Array.isArray(plan.week_template_phase2) && week > Math.ceil(totalWeeks / 2)
    const template = usePhase2 ? plan.week_template_phase2! : plan.week_template
    for (const day of template.filter(day => !day.is_rest)) {
      total++
      if (checked[`w${week}_d${day.day_of_week}_done`]) done++
    }
  }

  return { done, total }
}

function loadTrainingStatus() {
  const plan = parseJson<PlanSummary | null>('af_pt_active_plan', null)
  if (!plan) {
    return {
      detail: 'Generate a plan from your latest score.',
      progressText: '0%',
      progressPct: 0,
      hasPlan: false,
    }
  }

  const progress = countTrainingProgress(plan)
  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0
  return {
    detail: progress.total ? `${progress.done} of ${progress.total} sessions complete` : 'Plan loaded, no sessions counted yet',
    progressText: `${pct}%`,
    progressPct: pct,
    hasPlan: true,
  }
}

function refreshDashboard() {
  quickStats.value = loadQuickStats()
  trainingStatus.value = loadTrainingStatus()
}

function handleCalculate() {
  calculate()
  refreshDashboard()
  if (result.value) {
    calculationPulse.value += 1
    isSheetOpen.value = false
  }
}

function handleClear() {
  clear()
  refreshDashboard()
}

function findTabButton(name: string): Element | null {
  return document.querySelector(`[onclick*="'${name}'"], [onclick*='"${name}"']`)
}

function openTrainingFlow() {
  window.dispatchEvent(new CustomEvent('ptr:open-training', { detail: { source: 'readiness-dashboard' } }))
  if (document.getElementById('panel-training')) {
    window.switchTab?.('training', findTabButton('training'))
    return
  }
  window.switchTab?.('ai', findTabButton('ai'))
}



onMounted(() => {
  // If dashboard is showing an "existing" score from history (after reload/clear),
  // hydrate window.lastScore so that Training generation can use the saved score
  // without forcing a fresh "record test" calculation.
  ensureLastScoreHydrated()

  refreshDashboard()
  window.addEventListener('ptr:plan-changed', refreshDashboard)
  window.addEventListener('ptr:training-progress-changed', refreshDashboard)
  window.addEventListener('ptr:score-ready', refreshDashboard)
  window.addEventListener('storage', refreshDashboard)
})

onUnmounted(() => {
  window.removeEventListener('ptr:plan-changed', refreshDashboard)
  window.removeEventListener('ptr:training-progress-changed', refreshDashboard)
  window.removeEventListener('ptr:score-ready', refreshDashboard)
  window.removeEventListener('storage', refreshDashboard)
})
</script>

<template>
  <div class="readiness-dashboard">
    <section class="readiness-hero card" aria-labelledby="readiness-title">
      <h2 id="readiness-title" class="readiness-sr">Readiness Dashboard</h2>
      <div class="readiness-hero__ring">
        <HeroScoreRing :scores="componentScores" :has-input="hasInput" :celebrate-key="calculationPulse" />
      </div>

      <div class="readiness-stats" aria-label="Readiness quick stats">
        <div v-for="stat in quickStats" :key="stat.label" class="readiness-stat" :class="`is-${stat.tone || 'muted'}`">
          <div class="readiness-stat__label">{{ stat.label }}</div>
          <div class="readiness-stat__value">{{ stat.value }}</div>
          <div class="readiness-stat__detail">{{ stat.detail }}</div>
        </div>
      </div>

      <div class="readiness-hero__actions">
        <button class="btn btn-primary readiness-primary" data-onboarding="record-test-btn" type="button" @click="isSheetOpen = true">
          Record Test
        </button>
      </div>
    </section>

    <section class="training-status card" aria-labelledby="training-status-title">
      <div class="training-status__header">
        <div>
          <div class="card-title" id="training-status-title">Training Status</div>
          <p>{{ trainingStatus.detail }}</p>
        </div>
        <button class="btn btn-secondary training-status__action" type="button" @click="openTrainingFlow">
          Open Training
        </button>
      </div>
      <div class="training-status__bar" aria-hidden="true">
        <span :style="{ width: trainingStatus.progressPct + '%' }" />
      </div>
      <div class="training-status__meta">
        <span>{{ trainingStatus.progressText }} complete</span>
        <span>{{ trainingStatus.hasPlan ? 'Active plan' : 'No active plan' }}</span>
      </div>
    </section>

    <RecordTestSheet
      v-if="isSheetOpen"
      :profile="profile"
      :push-ups="pushUps"
      :core="core"
      :cardio="cardio"
      :whtr="whtr"
      :component-scores="componentScores"
      :any-exempt="anyExempt"
      @close="isSheetOpen = false"
      @calculate="handleCalculate"
      @clear="handleClear"
    />
  </div>
</template>

<style scoped>
.readiness-dashboard {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.readiness-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 14px;
  overflow: hidden;
}
.readiness-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.readiness-hero__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
}
.readiness-primary {
  min-height: 46px;
  min-width: 168px;
}
.readiness-link {
  border: 0;
  background: transparent;
  color: var(--accent);
  cursor: pointer;
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  text-decoration: underline;
  text-underline-offset: 4px;
}
.readiness-link:hover,
.readiness-link:focus-visible {
  color: var(--text);
}
.readiness-hero__ring {
  display: flex;
  justify-content: center;
  width: 100%;
  min-width: 0;
}
.readiness-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  width: 100%;
}
.readiness-stat {
  min-height: 76px;
  padding: 10px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 6px;
}
.readiness-stat__label {
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--muted);
}
.readiness-stat__value {
  margin-top: 6px;
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 24px;
  font-weight: 900;
  line-height: 1;
  color: var(--text);
}
.readiness-stat__detail {
  margin-top: 5px;
  font-size: 11px;
  line-height: 1.25;
  color: var(--muted);
}
.readiness-stat.is-good {
  border-color: color-mix(in srgb, var(--green) 50%, var(--border));
}
.readiness-stat.is-good .readiness-stat__value {
  color: var(--green);
}
.readiness-stat.is-warn {
  border-color: color-mix(in srgb, var(--orange) 50%, var(--border));
}
.readiness-stat.is-warn .readiness-stat__value {
  color: var(--orange);
}
.training-status {
  padding: 14px 16px;
}
.training-status :deep(.card-title) {
  margin-bottom: 8px;
}
.training-status__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.training-status p {
  margin: 0;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.35;
}
.training-status__action {
  flex-shrink: 0;
  min-height: 38px;
  padding: 9px 16px;
  font-size: 12px;
}
.training-status__bar {
  height: 7px;
  margin-top: 12px;
  overflow: hidden;
  background: var(--border);
  border-radius: 999px;
}
.training-status__bar span {
  display: block;
  height: 100%;
  min-width: 2px;
  background: linear-gradient(90deg, var(--accent), var(--green));
  border-radius: inherit;
  transition: width 0.24s ease;
}
.training-status__meta {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 7px;
  color: var(--muted);
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
}

@media (prefers-reduced-motion: reduce) {
  .training-status__bar span {
    transition: none;
  }
}

@media (max-width: 760px) {
  .readiness-hero {
    padding: 12px 10px;
  }
  .readiness-hero__actions {
    justify-content: center;
  }
  .training-status__header {
    align-items: stretch;
    flex-direction: row;
  }
  .training-status__action {
    white-space: nowrap;
  }
}

@media (max-width: 420px) {
  .readiness-stat {
    min-height: 72px;
    padding: 9px 7px;
  }
  .readiness-stat__value {
    font-size: 21px;
  }
  .readiness-stat__detail {
    font-size: 10px;
  }
  .readiness-primary {
    min-width: 0;
    flex: 1 1 150px;
  }
}
</style>
